import { DismissalType, DismissedCTA } from './dashboardTypes';

const STORAGE_KEY = 'rr_dismissed_ctas';
const TEMP_DISMISSAL_DAYS = 7;

/**
 * Dismisses a CTA with the specified dismissal type
 * @param ctaId - Unique identifier for the CTA
 * @param type - Type of dismissal (session, temporary, permanent)
 */
export function dismissCTA(ctaId: string, type: DismissalType): void {
  const now = Date.now();
  let expiresAt: number | 'permanent' | null;

  switch (type) {
    case 'permanent':
      expiresAt = 'permanent';
      break;
    case 'temporary':
      expiresAt = now + (TEMP_DISMISSAL_DAYS * 24 * 60 * 60 * 1000);
      break;
    case 'session':
      // Store in sessionStorage instead of localStorage
      sessionStorage.setItem(`rr_dismissed_${ctaId}`, 'true');
      return;
  }

  // Get existing dismissed CTAs
  const dismissed = getDismissedCTAs();

  // Add new dismissal
  dismissed[ctaId] = { ctaId, dismissedAt: now, expiresAt };

  // Save to localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
}

/**
 * Checks if a CTA is currently dismissed
 * @param ctaId - Unique identifier for the CTA
 * @returns true if the CTA is dismissed, false otherwise
 */
export function isDismissed(ctaId: string): boolean {
  // Check session dismissal first
  if (sessionStorage.getItem(`rr_dismissed_${ctaId}`)) {
    return true;
  }

  // Check persistent dismissal
  const dismissed = getDismissedCTAs();
  const item = dismissed[ctaId];

  if (!item) return false;

  // Permanent dismissal
  if (item.expiresAt === 'permanent') return true;

  // Temporary dismissal - check if still valid
  if (typeof item.expiresAt === 'number' && Date.now() < item.expiresAt) {
    return true;
  }

  // Expired - cleanup and return false
  delete dismissed[ctaId];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
  return false;
}

/**
 * Gets all dismissed CTAs from localStorage
 * @returns Record of dismissed CTAs by ID
 */
export function getDismissedCTAs(): Record<string, DismissedCTA> {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return {};

  try {
    const parsed = JSON.parse(stored);

    // Clean up expired dismissals
    const now = Date.now();
    const cleaned: Record<string, DismissedCTA> = {};

    Object.entries(parsed).forEach(([id, item]: [string, any]) => {
      if (item.expiresAt === 'permanent' ||
          (typeof item.expiresAt === 'number' && item.expiresAt > now)) {
        cleaned[id] = item;
      }
    });

    // Save cleaned version if any were removed
    if (Object.keys(cleaned).length !== Object.keys(parsed).length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
    }

    return cleaned;
  } catch {
    return {};
  }
}

/**
 * Undismisses a CTA (removes it from dismissed list)
 * @param ctaId - Unique identifier for the CTA
 */
export function undismissCTA(ctaId: string): void {
  // Remove from sessionStorage
  sessionStorage.removeItem(`rr_dismissed_${ctaId}`);

  // Remove from localStorage
  const dismissed = getDismissedCTAs();
  delete dismissed[ctaId];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
}

/**
 * Clears all dismissed CTAs
 */
export function clearDismissals(): void {
  localStorage.removeItem(STORAGE_KEY);

  // Clear all session storage dismissals
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('rr_dismissed_')) {
      sessionStorage.removeItem(key);
    }
  });
}

/**
 * Gets count of dismissed CTAs
 * @returns Number of currently dismissed CTAs
 */
export function getDismissedCount(): number {
  const dismissed = getDismissedCTAs();

  // Count session dismissals
  let sessionCount = 0;
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('rr_dismissed_')) {
      sessionCount++;
    }
  });

  return Object.keys(dismissed).length + sessionCount;
}

/**
 * Auto-dismisses CTAs when user completes related actions
 * @param action - Action that was completed
 */
export function autoDissmissOnAction(action: 'first_role' | 'first_candidate' | 'ats_connected'): void {
  switch (action) {
    case 'first_role':
      dismissCTA('add-first-role', 'permanent');
      dismissCTA('add-role-cta', 'permanent');
      break;
    case 'first_candidate':
      dismissCTA('add-first-candidate', 'permanent');
      dismissCTA('add-candidate-cta', 'permanent');
      break;
    case 'ats_connected':
      dismissCTA('connect-ats', 'permanent');
      break;
  }
}
